import { useEffect, useState } from "react";
import { Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import LeadInbox from "@/components/LeadInbox";

const inputCls = "w-full h-14 border border-border px-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent";

export default function ProDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(undefined);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({
    company_name: "", phone: "", website: "", years_experience: "",
    description: "", category_slugs: [], city_slugs: [], zip_codes: "",
  });

  useEffect(() => {
    api.get("/professionals/me").then((r) => setProfile(r.data)).catch(() => setProfile({ application_status: null }));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  const toggle = (key, val) =>
    setF((p) => ({ ...p, [key]: p[key].includes(val) ? p[key].filter((x) => x !== val) : [...p[key], val] }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/professionals/apply", {
        ...f,
        years_experience: f.years_experience ? parseInt(f.years_experience, 10) : null,
        zip_codes: f.zip_codes.split(",").map((z) => z.trim()).filter((z) => /^\d{5}$/.test(z)),
      });
      setProfile(data);
      toast.success("Application submitted for review");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (profile === undefined) {
    return (
      <div className="min-h-screen bg-[#F4F5F7]"><AppHeader />
        <p className="text-center text-slate-400 text-sm py-24">Loading…</p>
      </div>
    );
  }

  const status = profile?.application_status;

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <AppHeader />
      <div className="max-w-2xl mx-auto px-6 py-12">
        <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-2">Professional Area</p>
        <h1 data-testid="pro-dashboard-heading" className="font-heading font-black tracking-tighter text-navy text-4xl">
          {user?.name}
        </h1>

        {status === "approved" ? (
          <>
            <div data-testid="pro-application-status" className="bg-white border border-border p-5 mt-8 flex items-center gap-3">
              <CheckCircle2 size={20} className="text-green-600 shrink-0" />
              <div>
                <p className="font-heading font-bold text-navy">{profile.company_name} — approved</p>
                <p className="text-slate-500 text-xs">You receive matched leads for {(profile.category_slugs || []).length} service{(profile.category_slugs || []).length !== 1 ? "s" : ""} in {(profile.city_slugs || []).length} cit{(profile.city_slugs || []).length !== 1 ? "ies" : "y"}.</p>
              </div>
            </div>
            <LeadInbox />
          </>
        ) : status ? (
          <div data-testid="pro-application-status" className="bg-white border border-border p-8 mt-10">
            <div className="flex items-center gap-3">
              {status === "pending" && <Clock size={22} className="text-flame" />}
              {status === "approved" && <CheckCircle2 size={22} className="text-green-600" />}
              {status === "rejected" && <XCircle size={22} className="text-red-500" />}
              <h2 className="font-heading font-black text-navy text-2xl tracking-tight">
                {status === "pending" && "Application under review"}
                {status === "approved" && "Application approved"}
                {status === "rejected" && "Application not approved"}
              </h2>
            </div>
            <p className="text-slate-500 text-sm mt-3">
              {status === "pending"
                ? "Our team is reviewing your company details. You'll be able to receive matched leads once approved."
                : status === "approved"
                ? "Lead matching and offers are coming in the next platform phase."
                : "Contact us for more information about your application."}
            </p>
            <dl className="divide-y divide-border text-sm mt-6">
              {[
                ["Company", profile.company_name],
                ["Phone", profile.phone],
                ["Services", (profile.category_slugs || []).join(", ")],
                ["Cities", (profile.city_slugs || []).join(", ")],
              ].map(([k, v]) => (
                <div key={k} className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-slate-400 font-medium">{k}</dt>
                  <dd className="col-span-2 text-navy">{v || "—"}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-border p-8 mt-10 space-y-6" data-testid="pro-apply-form">
            <div>
              <h2 className="font-heading font-black text-navy text-2xl tracking-tight">Apply to join Prime Pro</h2>
              <p className="text-slate-500 text-sm mt-2">Your application will be reviewed before you can receive leads. Verification badges are granted only after documents are confirmed.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Company name</label>
              <input data-testid="pro-company-input" required value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Business phone</label>
                <input data-testid="pro-phone-input" required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Years of experience</label>
                <input data-testid="pro-years-input" type="number" min="0" value={f.years_experience} onChange={(e) => setF({ ...f, years_experience: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Website (optional)</label>
              <input data-testid="pro-website-input" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="https://" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">Business description</label>
              <textarea data-testid="pro-description-input" required minLength={10} rows={4} value={f.description}
                onChange={(e) => setF({ ...f, description: e.target.value })}
                className="w-full border border-border px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent" />
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-3">Services offered</label>
              <div className="flex flex-wrap gap-2" data-testid="pro-categories-select">
                {categories.map((c) => (
                  <button key={c.id} type="button" data-testid={`pro-cat-${c.slug}`} onClick={() => toggle("category_slugs", c.slug)}
                    className={`border px-4 py-2 text-xs font-medium transition-colors ${
                      f.category_slugs.includes(c.slug) ? "border-flame bg-orange-50 text-navy" : "border-border text-slate-500 hover:border-slate-300"
                    }`}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-3">Cities served</label>
              <div className="flex flex-wrap gap-2" data-testid="pro-cities-select">
                {locations.map((l) => (
                  <button key={l.id} type="button" data-testid={`pro-city-${l.slug}`} onClick={() => toggle("city_slugs", l.slug)}
                    className={`border px-4 py-2 text-xs font-medium transition-colors ${
                      f.city_slugs.includes(l.slug) ? "border-flame bg-orange-50 text-navy" : "border-border text-slate-500 hover:border-slate-300"
                    }`}>
                    {l.city}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-navy block mb-2">ZIP codes served (comma-separated, optional)</label>
              <input data-testid="pro-zips-input" value={f.zip_codes} onChange={(e) => setF({ ...f, zip_codes: e.target.value })} placeholder="35801, 35758" className={inputCls} />
            </div>
            <button data-testid="pro-apply-submit-btn"
              disabled={submitting || !f.company_name || !f.phone || f.description.length < 10 || !f.category_slugs.length || !f.city_slugs.length}
              className="w-full bg-flame hover:bg-flame-dark disabled:opacity-40 text-white font-heading font-bold uppercase tracking-wider text-sm py-4 transition-colors">
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
